import React, { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { thirdStepConfirmValidator } from "../../Validation/ThirdStepValidator";

import { Input, Submit, SubmitType } from "..";
import { FieldsSize } from "../../Helpers/interfaces";
import { useLabels } from "../../Hooks/useLabels";
import { CloseButton } from "../../CloseButton/CloseButton";
import { FieldStatus } from "../../Helpers/FieldStatus";
import { ErrorMessage } from "../../NoAppointments/ErrorMessage";
import { LoadingAnimation } from "../../../LoadingAnimation/LoadingAnimation";
import { useHandleClickAway } from "../../Hooks/useHandleClickAway";
import ClickAwayListener from "react-click-away-listener";
import classNames from "classnames";

type ConfirmationModalProps = {
  onClose: () => void;
  onSubmit: (code: number) => void;
  isOpen: boolean;
  Modal: any;
  loading: boolean;
  confirmationError: string;
  content: string;
  heading: string;
  buttonLabel: string;
  inputLabel: string;
  addResetCallback?: (callback: () => void) => void;
};

type ConfirmationModalFormType = {
  smsCode: number;
};

export const ConfirmationModal: FC<ConfirmationModalProps> = ({
  onClose,
  onSubmit,
  isOpen,
  Modal,
  loading,
  confirmationError,
  content,
  heading,
  buttonLabel,
  inputLabel,
  addResetCallback,
}) => {
  const labels = useLabels();
  const {
    handleMouseUp,
    handleMouseDown,
    handleClickAway,
    areaRef,
  } = useHandleClickAway(onClose);

  const {
    handleSubmit,
    getValues,
    formState: { errors },
    register,
    reset,
  } = useForm<ConfirmationModalFormType>({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: yupResolver(
      thirdStepConfirmValidator(labels.bookingLabels!.bookingErrors)
    ),
  });

  useEffect(() => {
    if (addResetCallback) addResetCallback(reset);
  }, []);

  function submitCode({ smsCode }: ConfirmationModalFormType) {
    onSubmit(smsCode);
  }

  return (
    <Modal
      bodyOpenClassName={"ReactModal__Body--open"}
      overlayClassName="react-modal__overlay"
      className={classNames("react-modal__content react-modal__content--additional", { 'react-modal__content--opened': isOpen })}
      isOpen={isOpen}
      shouldCloseOnEsc
      onRequestClose={onClose}
    >
      <div className="confirm-modal__outer">
        <ClickAwayListener onClickAway={handleClickAway}>
          <div className="confirm-modal__inner my-2 p-2 pt-1 fs-5" ref={areaRef} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
            <CloseButton onClick={onClose} withWhiteBg />
            <h4>{heading}</h4>
            <div
              dangerouslySetInnerHTML={{
                __html: content,
              }}
            />
            {confirmationError && <ErrorMessage message={confirmationError} />}
            <div className="mt-4">
              <form onSubmit={handleSubmit(submitCode)}>
                <div className="d-sm-flex">
                  <div className="w-100 pe-sm-1">
                    <Input
                      autoFocus
                      error={errors.smsCode}
                      getValues={getValues}
                      label={inputLabel}
                      name="smsCode"
                      type="number"
                      register={register}
                      inputmode="numeric"
                      autocomplete="one-time-code"
                      pattern="\d{6}"
                      size={FieldsSize.fullWidth}
                      reversedColors
                    />
                  </div>
                  <div className="w-100 ps-sm-1">
                    <Submit
                      type={SubmitType.submit}
                      status={
                        !!errors.smsCode || loading
                          ? FieldStatus.disabled
                          : FieldStatus.active
                      }
                    >
                      {loading ? (
                        <LoadingAnimation text={buttonLabel} />
                      ) : (
                        buttonLabel
                      )}
                    </Submit>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </ClickAwayListener>
      </div>
    </Modal>
  );
};
